import type { z } from 'zod';

/**
 * Validates an environment-derived config, throwing a plain `Error` when it does not match.
 *
 * A raw `ZodError` must not escape here. These schemas are parsed while the server module graph
 * loads, so a failure surfaces inside the Next.js instrumentation hook, which decorates the thrown
 * error by assigning to `.message`. `ZodError.prototype.message` is a getter without a setter, so
 * that assignment throws a `TypeError` that replaces this failure with an unreadable
 * "Cannot set property message of [object Object] which has only a getter" at the request handler
 * boundary. Reporting the offending fields on a plain `Error` keeps the real cause visible.
 */
export const parseConfig = <Schema extends z.ZodTypeAny>(
    name: string,
    schema: Schema,
    input: unknown,
): z.infer<Schema> => {
    const result = schema.safeParse(input);

    if (result.success) {
        return result.data;
    }

    const issues = result.error.issues
        .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
        .join('\n');

    const message = `Invalid ${name}. Check the environment variables backing these fields:\n${issues}`;

    console.error(message);

    throw new Error(message);
};
